import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Avatar,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconCircleCheck,
  IconAlertTriangle,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";
import { buildAppendedPageContent } from "./dealAppendedPage";

const emptyLine = () => ({ revenue_amount: "", percentage: "" });

const UPFRONT_PAY_TYPE_LABELS = {
  FIXED_AMOUNT_PER_ACCOUNT: "Fixed Amount Per Account",
  SCALE_AMOUNT_PER_ACCOUNT: "Scaled Amount Per Account",
  SCALE_AMOUNT_TOTAL: "Scaled Amount Total",
  FIXED_PERCENT_ACV: "Fixed Percentage of ACV",
  SCALE_PERCENT_ACV: "Scaled Percentage of ACV",
  INITIAL_PRICE: "Initial Price",
};

// Upfront pay types that are driven by editable scale lines.
const UPFRONT_SCALE_TYPES = [
  "SCALE_AMOUNT_PER_ACCOUNT",
  "SCALE_AMOUNT_TOTAL",
  "SCALE_PERCENT_ACV",
];

// State key for the single (non-split) upfront pay selection.
const UPFRONT_SINGLE_SCOPE = "__single__";

// Editable values seeded from an upfront pay option's defaults.
const upfrontValuesFromOption = opt => ({
  fixedAmount: opt?.fixed_amount ?? "",
  fixedPercentage: opt?.fixed_percentage ?? "",
  scaleLines: Array.isArray(opt?.scale_lines)
    ? opt.scale_lines.map(l => ({
        accounts_amount: l.accounts_amount ?? "",
        amount: l.amount ?? "",
        percentage: l.percentage ?? "",
      }))
    : [],
});

// In edit mode, seed editable upfront values from the deal user's saved
// overrides (fixed amount / percentage / scale lines), falling back to the
// selected option's defaults for any value the deal doesn't override.
const upfrontValuesFromDealUser = (user, opt) => {
  const base = upfrontValuesFromOption(opt);
  if (!user) return base;
  const savedLines = Array.isArray(user.upfront_scale_lines)
    ? user.upfront_scale_lines
    : [];
  return {
    fixedAmount:
      user.upfront_pay_fixed_amount != null
        ? user.upfront_pay_fixed_amount
        : base.fixedAmount,
    fixedPercentage:
      user.upfront_pay_fixed_percentage != null
        ? user.upfront_pay_fixed_percentage
        : base.fixedPercentage,
    scaleLines:
      savedLines.length > 0
        ? savedLines.map(l => ({
            accounts_amount: l.accounts_amount ?? "",
            amount: l.amount ?? "",
            percentage: l.percentage ?? "",
          }))
        : base.scaleLines,
  };
};

// Styling for the live-generated page appended below the document preview.
// Mimics a clean printed agreement page (serif type, document-style table).
const appendedPageStyles = {
  page: {
    backgroundColor: "#fff",
    padding: "40px 44px",
    fontFamily: "Georgia, 'Times New Roman', serif",
    color: "#1a1a1a",
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: "#722d11",
    fontWeight: 700,
    marginBottom: 12,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    borderBottom: "2px solid #1a1a1a",
    padding: "6px 8px",
    fontSize: 13,
    fontWeight: 700,
  },
  td: {
    padding: "6px 8px",
    borderBottom: "1px solid #ddd",
    fontSize: 13,
  },
};

function DealFormPage({ mode = "add", dealId = null }) {
  const isEdit = mode === "edit";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();
  const [savingDeal, setSavingDeal] = useState(false);
  const [deletingDeal, setDeletingDeal] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [loadingDealOptions, setLoadingDealOptions] = useState(false);
  const [loadingDeal, setLoadingDeal] = useState(isEdit);
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedSellingYearId, setSelectedSellingYearId] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [dealFor, setDealFor] = useState("USER");
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [mdList, setMdList] = useState([]);
  const [selectedParentMdId, setSelectedParentMdId] = useState(null);
  const [defaultDeals, setDefaultDeals] = useState([]);
  const [selectedDefaultDealId, setSelectedDefaultDealId] = useState(null);
  // When the selected default deal isn't customizable, its fields are shown but
  // locked for editing.
  const [defaultDealCustomizable, setDefaultDealCustomizable] = useState(true);
  const [marketingPartners, setMarketingPartners] = useState([]);
  // Per scale type: whether the scale applies to all marketing partners, the
  // partners for the primary scale, and any additional partner-specific scales.
  const [scaleApplyAll, setScaleApplyAll] = useState({
    COMMISSION: true,
    MD: true,
    OVERRIDE: true,
  });
  const [scalePartnerIds, setScalePartnerIds] = useState({
    COMMISSION: [],
    MD: [],
    OVERRIDE: [],
  });
  const [extraScales, setExtraScales] = useState({
    COMMISSION: [],
    MD: [],
    OVERRIDE: [],
  });
  const [dealType, setDealType] = useState("REP");
  const [minimumOverride, setMinimumOverride] = useState(false);
  const [minimumOverridePercentage, setMinimumOverridePercentage] =
    useState("");
  const [minimumOverrideMdIds, setMinimumOverrideMdIds] = useState([]);
  const [minimumOverridePercentages, setMinimumOverridePercentages] = useState(
    {}
  );
  const [customMinimumOverrides, setCustomMinimumOverrides] = useState([]);
  const [customOverrideCounter, setCustomOverrideCounter] = useState(0);
  const [splitMd, setSplitMd] = useState(false);
  const [splitMdUserIds, setSplitMdUserIds] = useState([]);
  const [splitMdPercentages, setSplitMdPercentages] = useState({});
  const [defaultScales, setDefaultScales] = useState({});
  const [commissionTemplateId, setCommissionTemplateId] = useState(null);
  const [commissionLines, setCommissionLines] = useState([emptyLine()]);
  const [commissionTemplateByUser, setCommissionTemplateByUser] = useState({});
  const [commissionLinesByUser, setCommissionLinesByUser] = useState({});
  const [overrideTemplateId, setOverrideTemplateId] = useState(null);
  const [overrideLines, setOverrideLines] = useState([emptyLine()]);
  const [hasOverrideOnRecruitedReps, setHasOverrideOnRecruitedReps] =
    useState(false);
  // Per-downline-user override scales (REP deals with override enabled).
  const [customUserOverrides, setCustomUserOverrides] = useState([]);
  const [customUserOverrideCounter, setCustomUserOverrideCounter] = useState(0);
  // Recruited reps the override applies to (display list, from the deal).
  const [overrideUsers, setOverrideUsers] = useState([]);
  const [mdTemplateId, setMdTemplateId] = useState(null);
  const [mdLines, setMdLines] = useState([emptyLine()]);
  const [upfrontPayOptions, setUpfrontPayOptions] = useState([]);
  const [selectedUpfrontPayId, setSelectedUpfrontPayId] = useState(null);
  const [upfrontPayByUser, setUpfrontPayByUser] = useState({});
  // Editable upfront pay values (fixed amount / percentage / scale lines),
  // keyed by scope: a split-deal user id, or UPFRONT_SINGLE_SCOPE.
  const [upfrontValuesByScope, setUpfrontValuesByScope] = useState({});
  // In edit mode, the loaded deal's per-scope upfront user objects, held until
  // the upfront pay options load so saved overrides can be seeded with the
  // option defaults as fallback.
  const [upfrontSeedByScope, setUpfrontSeedByScope] = useState(null);
  const [splitActiveTab, setSplitActiveTab] = useState(null);

  const fetchSellingYears = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v2/selling-year/list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const years = Array.isArray(result) ? result : result.data || [];
        setSellingYears(years);
        // In edit mode the selling year comes from the loaded deal, so don't
        // override it with the active/default year here.
        if (!isEdit) {
          const unlockedYears = years.filter(y => !y.is_locked);
          const activeYear = unlockedYears.find(y => y.is_active);
          const defaultYear = activeYear || unlockedYears[0];
          if (defaultYear) {
            setSelectedSellingYearId(defaultYear.id);
          }
        }
      }
    } catch (err) {}
  }, [getVerifiedJWT, isEdit]);

  const fetchDealOptions = useCallback(async () => {
    if (!selectedSellingYearId) return;
    try {
      setLoadingDealOptions(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/pyr/deal-data/${selectedSellingYearId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.data ?? result;
        setUsers(Array.isArray(data.users) ? data.users : []);
        setLeads(Array.isArray(data.leads) ? data.leads : []);
        setMdList(Array.isArray(data.md_list) ? data.md_list : []);
        setDefaultDeals(
          Array.isArray(data.default_deals) ? data.default_deals : []
        );
        setMarketingPartners(
          Array.isArray(data.marketing_partners) ? data.marketing_partners : []
        );
        const scales =
          data.default_scales && typeof data.default_scales === "object"
            ? data.default_scales
            : {};
        setDefaultScales(scales);
        // For a new deal, default each scale to the first available option
        // (and populate its lines). In edit mode the values come from the
        // loaded deal instead.
        if (!isEdit) {
          const applyFirstScale = (type, setTemplateId, setLines) => {
            const first = Array.isArray(scales[type]) ? scales[type][0] : null;
            if (!first) return;
            setTemplateId(String(first.id));
            if (Array.isArray(first.scale_lines)) {
              setLines(
                first.scale_lines.map(l => ({
                  revenue_amount: l.revenue_amount ?? "",
                  percentage: l.percentage ?? "",
                }))
              );
            }
          };
          applyFirstScale(
            "COMMISSION",
            setCommissionTemplateId,
            setCommissionLines
          );
          applyFirstScale("MD", setMdTemplateId, setMdLines);
          applyFirstScale("OVERRIDE", setOverrideTemplateId, setOverrideLines);
        }
        const upfrontOptions = Array.isArray(data.upfront_pay_options)
          ? data.upfront_pay_options
          : [];
        setUpfrontPayOptions(upfrontOptions);
        // In edit mode the upfront pay comes from the loaded deal; don't
        // pre-select the default option.
        if (!isEdit) {
          const defaultUpfront = upfrontOptions.find(o => o.is_default);
          setSelectedUpfrontPayId(defaultUpfront ? defaultUpfront.id : null);
          if (defaultUpfront) {
            setUpfrontValuesByScope({
              [UPFRONT_SINGLE_SCOPE]: upfrontValuesFromOption(defaultUpfront),
            });
          }
        }
      }
    } catch (err) {
    } finally {
      setLoadingDealOptions(false);
    }
  }, [getVerifiedJWT, selectedSellingYearId, isEdit]);

  useEffect(() => {
    fetchSellingYears();
  }, [fetchSellingYears]);

  // When arriving with a ?lead_id=... param (e.g. from the recruiting
  // pipeline), switch the toggle to Lead and pre-select that lead.
  useEffect(() => {
    const leadIdParam = searchParams.get("lead_id");
    if (leadIdParam) {
      setDealFor("LEAD");
      setSelectedLeadId(Number(leadIdParam));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchDealOptions();
  }, [fetchDealOptions]);

  // In edit mode, load the existing deal and prepopulate the whole form.
  const fetchDealDetails = useCallback(async () => {
    if (!isEdit || !dealId) return;
    try {
      setLoadingDeal(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint(`/v1/pyr/deal/${dealId}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) return;
      const result = await response.json();
      const deal = result?.data ?? result;
      if (!deal) return;

      const linesFromScale = scale =>
        Array.isArray(scale?.scale_lines) && scale.scale_lines.length > 0
          ? scale.scale_lines.map(l => ({
              revenue_amount: l.revenue_amount ?? "",
              percentage: l.percentage ?? "",
            }))
          : [emptyLine()];

      // Normalize a `*_scales` array (fall back to the legacy singular scale).
      const scaleArray = (arr, single) =>
        Array.isArray(arr) && arr.length > 0 ? arr : single ? [single] : [];

      // Populate a scale type's primary fields plus its marketing-partner
      // config from the loaded scales. A single scale is treated as
      // "apply to all"; multiple scales are partner-specific.
      const applyScalesArray = (type, scales, setTemplateId, setLines) => {
        const list = Array.isArray(scales) ? scales : [];
        const primary = list[0];
        setTemplateId(
          primary?.template_id != null ? String(primary.template_id) : null
        );
        setLines(linesFromScale(primary));
        setScaleApplyAll(prev => ({ ...prev, [type]: list.length <= 1 }));
        setScalePartnerIds(prev => ({
          ...prev,
          [type]:
            list.length > 1
              ? (primary?.marketing_partner_ids ?? []).map(String)
              : [],
        }));
        setExtraScales(prev => ({
          ...prev,
          [type]: list.slice(1).map(s => ({
            templateId: s.template_id != null ? String(s.template_id) : null,
            lines: linesFromScale(s),
            marketing_partner_ids: (s.marketing_partner_ids ?? []).map(String),
          })),
        }));
      };

      const dType = deal.deal_type ?? "REP";
      const dealUsers = Array.isArray(deal.users) ? deal.users : [];

      // Top-level deal fields
      if (deal.selling_year_id != null) {
        setSelectedSellingYearId(deal.selling_year_id);
      }
      setDealType(dType);
      setSelectedParentMdId(deal.parent_md_id ?? null);
      const defaultDealIdValue =
        deal.default_ps_selling_year_deal_id ??
        deal.default_deal_id ??
        deal.default_deal?.id ??
        null;
      setSelectedDefaultDealId(
        defaultDealIdValue != null ? String(defaultDealIdValue) : null
      );

      // MD-specific fields
      if (dType === "MD") {
        applyScalesArray(
          "MD",
          scaleArray(deal.md_scales, deal.md_scale),
          setMdTemplateId,
          setMdLines
        );
        setSplitMd(dealUsers.length > 1);

        const minOverride = deal.has_minimum_override;
        setMinimumOverride(!!minOverride);
        if (minOverride) {
          const overrideRows = Array.isArray(deal.minimum_overrides)
            ? deal.minimum_overrides
            : [];

          // General override rows have no user/specific-deal target; custom
          // (per-user) rows carry a temp_user_id or specific_ps_selling_year_*.
          const generalRows = overrideRows.filter(
            r =>
              r.temp_user_id == null &&
              r.specific_ps_selling_year_deal_id == null
          );
          const customRows = overrideRows.filter(
            r =>
              r.temp_user_id != null ||
              r.specific_ps_selling_year_deal_id != null
          );

          setMinimumOverridePercentage(
            generalRows[0]?.total_minimum_override_percentage ?? ""
          );
          setMinimumOverrideMdIds(
            generalRows.map(r => String(r.parent_ps_selling_year_deal_id))
          );
          setMinimumOverridePercentages(
            generalRows.reduce((acc, r) => {
              acc[String(r.parent_ps_selling_year_deal_id)] =
                r.parent_ps_selling_year_deal_percentage;
              return acc;
            }, {})
          );

          // Group custom override rows by their target user.
          const customByUser = customRows.reduce((acc, r) => {
            const key = String(
              r.temp_user_id ?? r.specific_ps_selling_year_deal_id
            );
            if (!acc[key]) acc[key] = [];
            acc[key].push(r);
            return acc;
          }, {});
          const customList = Object.entries(customByUser).map(
            ([userKey, rows], i) => ({
              key: i,
              userId: rows[0]?.temp_user_id != null ? userKey : null,
              percentage: rows[0]?.total_minimum_override_percentage ?? "",
              parentMdIds: rows.map(r =>
                String(r.parent_ps_selling_year_deal_id)
              ),
              parentMdPercentages: rows.reduce((acc, r) => {
                acc[String(r.parent_ps_selling_year_deal_id)] =
                  r.parent_ps_selling_year_deal_percentage;
                return acc;
              }, {}),
            })
          );
          setCustomMinimumOverrides(customList);
          setCustomOverrideCounter(customList.length);
        }
      }

      // Users / leads
      if (dType === "MD" && dealUsers.length > 1) {
        // Split MD: first user is the primary, the rest are additional.
        const primary = dealUsers[0];
        const primaryId = primary?.user_id ?? primary?.id;
        if (primaryId != null) setSelectedUserId(primaryId);
        setDealFor("USER");
        setSplitMdUserIds(
          dealUsers.map(u => String(u.user_id ?? u.id)).filter(Boolean)
        );
        setSplitMdPercentages(
          dealUsers.reduce((acc, u) => {
            acc[String(u.user_id ?? u.id)] = u.deal_percentage ?? 0;
            return acc;
          }, {})
        );
        const templByUser = {};
        const linesByUser = {};
        const upfrontByUser = {};
        const upfrontSeed = {};
        dealUsers.forEach(u => {
          const key = String(u.user_id ?? u.id);
          const userCommission = scaleArray(
            u.commission_scales,
            u.commission_scale
          )[0];
          templByUser[key] =
            userCommission?.template_id != null
              ? String(userCommission.template_id)
              : null;
          linesByUser[key] = linesFromScale(userCommission);
          if (u.upfront_pay_id != null) upfrontByUser[key] = u.upfront_pay_id;
          upfrontSeed[key] = u;
        });
        setCommissionTemplateByUser(templByUser);
        setCommissionLinesByUser(linesByUser);
        setUpfrontPayByUser(upfrontByUser);
        setUpfrontSeedByScope(upfrontSeed);
      } else {
        const primary = dealUsers[0];
        if (primary?.lead_id != null) {
          setDealFor("LEAD");
          setSelectedLeadId(primary.lead_id);
        } else if (primary) {
          setDealFor("USER");
          setSelectedUserId(primary.user_id ?? primary.id ?? null);
        }
        applyScalesArray(
          "COMMISSION",
          scaleArray(primary?.commission_scales, primary?.commission_scale),
          setCommissionTemplateId,
          setCommissionLines
        );
        if (primary?.upfront_pay_id != null) {
          setSelectedUpfrontPayId(primary.upfront_pay_id);
        }
        if (primary) {
          setUpfrontSeedByScope({ [UPFRONT_SINGLE_SCOPE]: primary });
        }
        if (dType === "REP") {
          // Override is stored at the deal level (not per user).
          const overrideScales = scaleArray(
            deal.override_scales,
            deal.override_scale
          );
          const hasOverride =
            deal.has_override_on_recruited_reps ?? overrideScales.length > 0;
          setHasOverrideOnRecruitedReps(!!hasOverride);
          if (hasOverride) {
            applyScalesArray(
              "OVERRIDE",
              overrideScales,
              setOverrideTemplateId,
              setOverrideLines
            );

            // Per-rep custom overrides come back as `rep_overrides`.
            const repOverrides = Array.isArray(deal.rep_overrides)
              ? deal.rep_overrides
              : [];
            setCustomUserOverrides(
              repOverrides.map((o, i) => ({
                key: i,
                userId:
                  o.recruited_user_id != null
                    ? String(o.recruited_user_id)
                    : null,
                templateId: null,
                lines: linesFromScale(
                  scaleArray(o.override_scales, o.scale)[0]
                ),
              }))
            );
            setCustomUserOverrideCounter(repOverrides.length);
          }
          setOverrideUsers(
            Array.isArray(deal.override_users) ? deal.override_users : []
          );
        }
      }
    } catch (err) {
    } finally {
      setLoadingDeal(false);
    }
  }, [getVerifiedJWT, isEdit, dealId]);

  useEffect(() => {
    fetchDealDetails();
  }, [fetchDealDetails]);

  // Once the upfront pay options are loaded, seed the editable upfront values
  // from the deal's saved overrides (using the option defaults as fallback for
  // anything the deal doesn't override).
  useEffect(() => {
    if (!upfrontSeedByScope || upfrontPayOptions.length === 0) return;
    setUpfrontValuesByScope(prev => {
      const next = { ...prev };
      Object.entries(upfrontSeedByScope).forEach(([scope, user]) => {
        const payId =
          scope === UPFRONT_SINGLE_SCOPE
            ? (user?.upfront_pay_id ?? null)
            : (upfrontPayByUser[scope] ?? user?.upfront_pay_id ?? null);
        const option =
          upfrontPayOptions.find(o => String(o.id) === String(payId)) ?? null;
        next[scope] = upfrontValuesFromDealUser(user, option);
      });
      return next;
    });
    setUpfrontSeedByScope(null);
  }, [upfrontSeedByScope, upfrontPayOptions, upfrontPayByUser]);

  // When minimum override is enabled, pre-select the chosen parent MD.
  useEffect(() => {
    if (minimumOverride && selectedParentMdId) {
      setMinimumOverrideMdIds(prev =>
        prev.includes(String(selectedParentMdId))
          ? prev
          : [String(selectedParentMdId), ...prev]
      );
    }
  }, [minimumOverride, selectedParentMdId]);

  const userLabel = u =>
    u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim();

  const leadLabel = l => {
    const name = `${l.first_name || ""} ${l.last_name || ""}`.trim();
    return name || l.email || l.phone_number || `Lead ${l.id}`;
  };

  const updateLine = (setLines, index, field, value) =>
    setLines(lines =>
      lines.map((line, i) => (i === index ? { ...line, [field]: value } : line))
    );

  const addLine = setLines => setLines(lines => [...lines, emptyLine()]);

  const removeLine = (setLines, index) =>
    setLines(lines =>
      lines.length > 1 ? lines.filter((_, i) => i !== index) : lines
    );

  // Fields populated from a non-customizable default deal are read-only.
  const fieldsDisabled =
    !!selectedDefaultDealId && defaultDealCustomizable === false;

  // Populate a scale's template + lines from a default-scale id (the ids stored
  // on a default deal). Clears the template when the id can't be matched.
  const applyScaleById = (type, scaleId, setTemplateId, setLines) => {
    const list = Array.isArray(defaultScales?.[type])
      ? defaultScales[type]
      : [];
    const scale = list.find(s => String(s.id) === String(scaleId));
    if (!scale) {
      setTemplateId(null);
      return;
    }
    setTemplateId(String(scale.id));
    if (Array.isArray(scale.scale_lines)) {
      setLines(
        scale.scale_lines.map(l => ({
          revenue_amount: l.revenue_amount ?? "",
          percentage: l.percentage ?? "",
        }))
      );
    }
  };

  // Selecting a default deal seeds the form (deal type, commission scale,
  // upfront pay, and either the MD scale or the override scale) from the deal.
  const handleDefaultDealChange = value => {
    setSelectedDefaultDealId(value);
    const deal = defaultDeals.find(d => String(d.id) === value);
    if (!deal) return;

    setDefaultDealCustomizable(deal.is_customizable !== false);

    // Reset marketing-partner scale config — a freshly selected deal starts out
    // applying to all marketing partners.
    setScaleApplyAll({ COMMISSION: true, MD: true, OVERRIDE: true });
    setScalePartnerIds({ COMMISSION: [], MD: [], OVERRIDE: [] });
    setExtraScales({ COMMISSION: [], MD: [], OVERRIDE: [] });

    const dType = deal.deal_type ?? "REP";
    setDealType(dType);

    applyScaleById(
      "COMMISSION",
      deal.commission_scale_id,
      setCommissionTemplateId,
      setCommissionLines
    );

    const upfrontOption = upfrontOptionById(deal.upfront_pay_id);
    setSelectedUpfrontPayId(
      deal.upfront_pay_id != null ? Number(deal.upfront_pay_id) : null
    );
    setUpfrontValuesByScope(prev => ({
      ...prev,
      [UPFRONT_SINGLE_SCOPE]: upfrontValuesFromOption(upfrontOption),
    }));

    if (dType === "MD") {
      applyScaleById("MD", deal.md_scale_id, setMdTemplateId, setMdLines);
    } else {
      setHasOverrideOnRecruitedReps(!!deal.allow_override);
      if (deal.allow_override) {
        applyScaleById(
          "OVERRIDE",
          deal.override_scale_id,
          setOverrideTemplateId,
          setOverrideLines
        );
      }
    }
  };

  const renderScaleSection = ({
    type,
    label,
    templateId,
    setTemplateId,
    lines,
    setLines,
    required,
    validateAgainstParent = false,
  }) => {
    const templates = Array.isArray(defaultScales?.[type])
      ? defaultScales[type]
      : [];

    const exceedsParentBase =
      validateAgainstParent &&
      parentBasePercentage != null &&
      lines.some(
        l => l.percentage !== "" && Number(l.percentage) > parentBasePercentage
      );

    // Warn (but allow) when a line is within 85%-100% of the parent base rate.
    const nearParentBase =
      validateAgainstParent &&
      parentBasePercentage != null &&
      !exceedsParentBase &&
      lines.some(
        l =>
          l.percentage !== "" &&
          Number(l.percentage) > parentBasePercentage * 0.85
      );

    const handleTemplateChange = value => {
      setTemplateId(value);
      const tmpl = templates.find(t => String(t.id) === value);
      if (tmpl && Array.isArray(tmpl.scale_lines)) {
        setLines(
          tmpl.scale_lines.map(l => ({
            revenue_amount: l.revenue_amount ?? "",
            percentage: l.percentage ?? "",
          }))
        );
      }
    };

    return (
      <Stack gap="xs">
        <Text fw={600} size="sm">
          {label}
          {required ? " *" : ""}
        </Text>
        <Select
          placeholder="Populate from default scale"
          data={templates.map(t => ({ value: String(t.id), label: t.name }))}
          value={templateId}
          onChange={handleTemplateChange}
          searchable
          clearable
          disabled={fieldsDisabled}
        />
        {exceedsParentBase && (
          <Text size="sm" c="red.7">
            Percentage cannot exceed the parent MD's base rate of{" "}
            {parentBasePercentage}%.
          </Text>
        )}
        {nearParentBase && (
          <Text size="sm" c="yellow.7">
            Percentage is above 85% of the parent MD's base rate of{" "}
            {parentBasePercentage}%.
          </Text>
        )}
        <Stack gap="xs">
          {lines.map((line, index) => (
            <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
              <NumberInput
                label={index === 0 ? "Revenue Amount" : undefined}
                placeholder="Revenue amount"
                value={line.revenue_amount}
                onChange={value =>
                  updateLine(setLines, index, "revenue_amount", value)
                }
                min={0}
                prefix="$"
                thousandSeparator=","
                decimalScale={2}
                fixedDecimalScale
                style={{ flex: 1 }}
                disabled={fieldsDisabled}
              />
              <NumberInput
                label={index === 0 ? "Percentage" : undefined}
                placeholder="Percentage"
                value={line.percentage}
                onChange={value =>
                  updateLine(setLines, index, "percentage", value)
                }
                min={0}
                suffix="%"
                style={{ flex: 1 }}
                disabled={fieldsDisabled}
              />
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => removeLine(setLines, index)}
                disabled={fieldsDisabled || lines.length === 1}
                aria-label="Remove scale line"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
          <Group justify="flex-start">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              disabled={fieldsDisabled}
              onClick={() => addLine(setLines)}
            >
              Add Scale Line
            </Button>
          </Group>
        </Stack>
      </Stack>
    );
  };

  // A marketing partner can only belong to one scale of a given type, so build
  // the option list excluding partners already assigned to a sibling scale.
  // scope is "primary" for the main scale or a numeric index for an extra one.
  const mpId = mp => mp.marketing_partner_id ?? mp.id;
  const marketingPartnerOptions = (type, scope) => {
    const used = new Set();
    if (scope !== "primary") {
      scalePartnerIds[type].forEach(id => used.add(String(id)));
    }
    extraScales[type].forEach((s, i) => {
      if (i !== scope) {
        s.marketing_partner_ids.forEach(id => used.add(String(id)));
      }
    });
    return marketingPartners
      .filter(mp => !used.has(String(mpId(mp))))
      .map(mp => ({ value: String(mpId(mp)), label: mp.name }));
  };

  const updateExtraScale = (type, index, producer) =>
    setExtraScales(prev => ({
      ...prev,
      [type]: prev[type].map((s, i) =>
        i === index ? { ...s, ...producer(s) } : s
      ),
    }));

  const addExtraScale = type =>
    setExtraScales(prev => ({
      ...prev,
      [type]: [
        ...prev[type],
        { templateId: null, lines: [emptyLine()], marketing_partner_ids: [] },
      ],
    }));

  const removeExtraScale = (type, index) =>
    setExtraScales(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));

  // Toggle + (when not "apply to all") the marketing-partner dropdown for the
  // primary scale and any additional partner-specific scales.
  const renderMarketingPartnerScales = (type, label) => {
    const applyAll = scaleApplyAll[type];
    return (
      <>
        <Switch
          label="Apply to all marketing partners"
          checked={applyAll}
          disabled={fieldsDisabled}
          onChange={event =>
            setScaleApplyAll(prev => ({
              ...prev,
              [type]: event.currentTarget.checked,
            }))
          }
        />

        {!applyAll && (
          <>
            <MultiSelect
              label="Marketing Partners"
              placeholder="Select marketing partners this scale applies to"
              data={marketingPartnerOptions(type, "primary")}
              value={scalePartnerIds[type]}
              onChange={value =>
                setScalePartnerIds(prev => ({ ...prev, [type]: value }))
              }
              searchable
              clearable
            />

            {extraScales[type].map((scale, index) => (
              <Paper key={index} withBorder p="sm" radius="md">
                <Stack gap="sm">
                  <Group justify="space-between" align="center">
                    <Text fw={600} size="sm">
                      {label} {index + 2}
                    </Text>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeExtraScale(type, index)}
                      aria-label="Remove scale"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>

                  {renderScaleSection({
                    type,
                    label: `${label} ${index + 2}`,
                    templateId: scale.templateId,
                    setTemplateId: value =>
                      updateExtraScale(type, index, () => ({
                        templateId: value,
                      })),
                    lines: scale.lines,
                    setLines: updater =>
                      updateExtraScale(type, index, s => ({
                        lines:
                          typeof updater === "function"
                            ? updater(s.lines)
                            : updater,
                      })),
                    required: true,
                  })}

                  <MultiSelect
                    label="Marketing Partners"
                    placeholder="Select marketing partners this scale applies to"
                    data={marketingPartnerOptions(type, index)}
                    value={scale.marketing_partner_ids}
                    onChange={value =>
                      updateExtraScale(type, index, () => ({
                        marketing_partner_ids: value,
                      }))
                    }
                    searchable
                    clearable
                  />
                </Stack>
              </Paper>
            ))}

            <Group justify="flex-start">
              <Button
                variant="light"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={() => addExtraScale(type)}
              >
                Add Scale
              </Button>
            </Group>
          </>
        )}
      </>
    );
  };

  // In edit mode we never fall back to the default upfront option — only the
  // values returned for the deal should be selected.
  const defaultUpfrontId = isEdit
    ? null
    : (upfrontPayOptions.find(o => o.is_default)?.id ?? null);

  const upfrontSelectionForScope = userId =>
    userId
      ? (upfrontPayByUser[userId] ?? defaultUpfrontId)
      : selectedUpfrontPayId;

  const upfrontOptionById = id =>
    upfrontPayOptions.find(o => String(o.id) === String(id)) ?? null;

  // The editable values for a scope, falling back to the selected option's
  // defaults when the user hasn't touched them yet.
  const upfrontValuesForScope = (scope, option) =>
    upfrontValuesByScope[scope] ?? upfrontValuesFromOption(option);

  // Switching the selected option re-seeds that scope's editable values from
  // the new option's defaults.
  const handleUpfrontChange = (value, userId) => {
    const id = value != null ? Number(value) : null;
    const scope = userId ?? UPFRONT_SINGLE_SCOPE;
    if (userId) {
      setUpfrontPayByUser(prev => ({ ...prev, [userId]: id }));
    } else {
      setSelectedUpfrontPayId(id);
    }
    setUpfrontValuesByScope(prev => ({
      ...prev,
      [scope]: upfrontValuesFromOption(upfrontOptionById(id)),
    }));
  };

  const updateUpfrontValue = (scope, field, value) =>
    setUpfrontValuesByScope(prev => ({
      ...prev,
      [scope]: { ...(prev[scope] ?? {}), [field]: value },
    }));

  const updateUpfrontScaleLine = (scope, index, field, value) =>
    setUpfrontValuesByScope(prev => {
      const current = prev[scope] ?? {};
      const lines = Array.isArray(current.scaleLines) ? current.scaleLines : [];
      return {
        ...prev,
        [scope]: {
          ...current,
          scaleLines: lines.map((l, i) =>
            i === index ? { ...l, [field]: value } : l
          ),
        },
      };
    });

  const addUpfrontScaleLine = scope =>
    setUpfrontValuesByScope(prev => {
      const current = prev[scope] ?? {};
      const lines = Array.isArray(current.scaleLines) ? current.scaleLines : [];
      return {
        ...prev,
        [scope]: {
          ...current,
          scaleLines: [
            ...lines,
            { accounts_amount: "", amount: "", percentage: "" },
          ],
        },
      };
    });

  const removeUpfrontScaleLine = (scope, index) =>
    setUpfrontValuesByScope(prev => {
      const current = prev[scope] ?? {};
      const lines = Array.isArray(current.scaleLines) ? current.scaleLines : [];
      return {
        ...prev,
        [scope]: {
          ...current,
          scaleLines: lines.filter((_, i) => i !== index),
        },
      };
    });

  const usersById = users.reduce((acc, u) => {
    acc[String(u.id)] = u;
    return acc;
  }, {});

  const selectedUser = selectedUserId
    ? usersById[String(selectedUserId)]
    : null;

  // Initial selected user first, then any additional split users (de-duped)
  const splitParticipantIds = (() => {
    const ids = [];
    if (selectedUserId) {
      ids.push(String(selectedUserId));
    }
    splitMdUserIds.forEach(id => {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    });
    return ids;
  })();

  const renderUserOption = ({ option }) => {
    const user = usersById[option.value];
    if (!user) return option.label;
    return (
      <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
        <Avatar src={user.profile_image_url} radius="xl" size="sm">
          {userLabel(user).charAt(0)}
        </Avatar>
        <Text size="sm" style={{ flex: 1 }}>
          {userLabel(user)}
        </Text>
        {user.has_deal ? (
          <IconCircleCheck size={18} color="var(--mantine-color-green-6)" />
        ) : (
          <IconAlertTriangle size={18} color="var(--mantine-color-yellow-6)" />
        )}
      </Group>
    );
  };

  const mdById = mdList.reduce((acc, md) => {
    acc[String(md.id)] = md;
    return acc;
  }, {});

  // A child's MD/commission scale cannot pay more than the parent MD's base
  // (first) tier. Null when no parent MD is selected or it has no scale.
  const parentMd = selectedParentMdId
    ? mdById[String(selectedParentMdId)]
    : null;
  const parentBasePercentage = Array.isArray(parentMd?.md_scale?.scale_lines)
    ? (parentMd.md_scale.scale_lines[0]?.percentage ?? null)
    : null;

  const mdLabel = md => {
    if (!md) return "";
    if (md.ps_deal_type === "COMPANY") return md.company_name;
    const recordUsers = Array.isArray(md.deal_users) ? md.deal_users : [];
    return recordUsers.map(u => userLabel(u)).join(", ");
  };

  const renderMdOption = ({ option }) => {
    const md = mdById[option.value];
    if (!md) return option.label;
    if (md.ps_deal_type === "COMPANY") {
      return (
        <Text size="sm" fw={600}>
          {md.company_name}
        </Text>
      );
    }
    const recordUsers = Array.isArray(md.deal_users) ? md.deal_users : [];
    return (
      <Group gap="lg" wrap="wrap" style={{ flex: 1 }}>
        {recordUsers.map(u => (
          <Group key={u.id} gap="xs" wrap="nowrap">
            <Avatar src={u.profile_image_url} radius="xl" size="sm">
              {(u.first_name || "?").charAt(0)}
            </Avatar>
            <Text size="sm">{userLabel(u)}</Text>
          </Group>
        ))}
      </Group>
    );
  };

  // Per-MD percentage rows + a total that turns green when it matches the
  // target percentage. Shared by the main and custom minimum-override blocks.
  const renderParentMdRows = (ids, percentages, onChangePercentage, target) => {
    const total = ids.reduce(
      (sum, id) => sum + (Number(percentages[id]) || 0),
      0
    );
    return (
      <>
        {ids.map(id => {
          const md = mdById[id];
          return (
            <Group key={id} wrap="nowrap" align="center" gap="sm">
              <Text size="sm" style={{ flex: 1 }}>
                {mdLabel(md) || "MD"}
              </Text>
              <NumberInput
                placeholder="Percentage"
                value={percentages[id] ?? ""}
                onChange={value => onChangePercentage(id, value)}
                min={0}
                suffix="%"
                required
                w={140}
              />
            </Group>
          );
        })}
        <Group justify="space-between">
          <Text fw={600} size="sm">
            Total
          </Text>
          <Text fw={700} c={total === target ? "green.7" : "red.7"}>
            {total}% / {target}%
          </Text>
        </Group>
      </>
    );
  };

  const downlineUserOptions = (
    selectedUser && Array.isArray(selectedUser.downline_users)
      ? selectedUser.downline_users
      : []
  ).map(u => ({ value: String(u.id), label: userLabel(u) }));

  // Reps recruited by the selected user — source for the REP custom override
  // user dropdown.
  const recruitedRepsOptions = (
    selectedUser && Array.isArray(selectedUser.recruited_reps)
      ? selectedUser.recruited_reps
      : []
  ).map(u => ({ value: String(u.user_id ?? u.id), label: userLabel(u) }));

  // Recruited reps the override applies to. In edit mode this comes from the
  // loaded deal; otherwise from the selected user's recruited reps.
  const overrideUsersList = isEdit
    ? overrideUsers
    : selectedUser && Array.isArray(selectedUser.recruited_reps)
      ? selectedUser.recruited_reps
      : [];

  const addCustomMinimumOverride = () => {
    setCustomMinimumOverrides(prev => [
      ...prev,
      {
        key: customOverrideCounter,
        userId: null,
        percentage: "",
        parentMdIds: [],
        parentMdPercentages: {},
      },
    ]);
    setCustomOverrideCounter(prev => prev + 1);
  };

  const updateCustomMinimumOverride = (index, patch) =>
    setCustomMinimumOverrides(prev =>
      prev.map((o, i) => (i === index ? { ...o, ...patch } : o))
    );

  const removeCustomMinimumOverride = index =>
    setCustomMinimumOverrides(prev => prev.filter((_, i) => i !== index));

  const addCustomUserOverride = () => {
    setCustomUserOverrides(prev => [
      ...prev,
      {
        key: customUserOverrideCounter,
        userId: null,
        templateId: null,
        lines: [emptyLine()],
      },
    ]);
    setCustomUserOverrideCounter(prev => prev + 1);
  };

  const updateCustomUserOverride = (index, patch) =>
    setCustomUserOverrides(prev =>
      prev.map((o, i) => (i === index ? { ...o, ...patch } : o))
    );

  const removeCustomUserOverride = index =>
    setCustomUserOverrides(prev => prev.filter((_, i) => i !== index));

  const renderCommissionScaleForUser = userId =>
    renderScaleSection({
      type: "COMMISSION",
      label: "Commission Scale",
      templateId: commissionTemplateByUser[userId] ?? null,
      setTemplateId: value =>
        setCommissionTemplateByUser(prev => ({ ...prev, [userId]: value })),
      lines: commissionLinesByUser[userId] ?? [emptyLine()],
      setLines: updater =>
        setCommissionLinesByUser(prev => ({
          ...prev,
          [userId]:
            typeof updater === "function"
              ? updater(prev[userId] ?? [emptyLine()])
              : updater,
        })),
      required: true,
      validateAgainstParent: true,
    });

  const renderUpfrontPaySection = (userId = null) => {
    if (upfrontPayOptions.length === 0) {
      return (
        <Text size="sm" c="dimmed">
          No upfront pay options for this year.
        </Text>
      );
    }

    const scope = userId ?? UPFRONT_SINGLE_SCOPE;
    const selectedId = upfrontSelectionForScope(userId);
    const option = upfrontOptionById(selectedId);
    const typeValue = option?.upfront_pay_type;
    const values = upfrontValuesForScope(scope, option);
    const isPercentScale = typeValue === "SCALE_PERCENT_ACV";
    const hasScale = UPFRONT_SCALE_TYPES.includes(typeValue);
    const scaleLines = Array.isArray(values.scaleLines)
      ? values.scaleLines
      : [];

    return (
      <Stack gap="sm">
        <Select
          label="Upfront Pay"
          placeholder="Select upfront pay"
          data={upfrontPayOptions.map(o => ({
            value: String(o.id),
            label: o.name,
          }))}
          value={selectedId != null ? String(selectedId) : null}
          onChange={value => handleUpfrontChange(value, userId)}
          searchable
          disabled={fieldsDisabled}
        />

        {option && (
          <Text size="sm" c="dimmed">
            {UPFRONT_PAY_TYPE_LABELS[typeValue] ?? typeValue}
          </Text>
        )}

        {typeValue === "FIXED_AMOUNT_PER_ACCOUNT" && (
          <NumberInput
            label="Fixed Amount"
            placeholder="Fixed amount"
            value={values.fixedAmount}
            onChange={value => updateUpfrontValue(scope, "fixedAmount", value)}
            min={0}
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            disabled={fieldsDisabled}
          />
        )}

        {typeValue === "FIXED_PERCENT_ACV" && (
          <NumberInput
            label="Fixed Percentage"
            placeholder="Fixed percentage"
            value={values.fixedPercentage}
            onChange={value =>
              updateUpfrontValue(scope, "fixedPercentage", value)
            }
            min={0}
            suffix="%"
            disabled={fieldsDisabled}
          />
        )}

        {hasScale && (
          <Stack gap="xs">
            <Text fw={600} size="sm">
              Scale Lines
            </Text>
            {scaleLines.map((line, index) => (
              <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
                <NumberInput
                  label={index === 0 ? "Account Number" : undefined}
                  placeholder="Account number"
                  value={line.accounts_amount}
                  onChange={value =>
                    updateUpfrontScaleLine(
                      scope,
                      index,
                      "accounts_amount",
                      value
                    )
                  }
                  min={0}
                  style={{ flex: 1 }}
                  disabled={fieldsDisabled}
                />
                {isPercentScale ? (
                  <NumberInput
                    label={index === 0 ? "Percentage of ACV" : undefined}
                    placeholder="Percentage"
                    value={line.percentage}
                    onChange={value =>
                      updateUpfrontScaleLine(scope, index, "percentage", value)
                    }
                    min={0}
                    suffix="%"
                    style={{ flex: 1 }}
                    disabled={fieldsDisabled}
                  />
                ) : (
                  <NumberInput
                    label={index === 0 ? "Amount" : undefined}
                    placeholder="Amount"
                    value={line.amount}
                    onChange={value =>
                      updateUpfrontScaleLine(scope, index, "amount", value)
                    }
                    min={0}
                    prefix="$"
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    style={{ flex: 1 }}
                    disabled={fieldsDisabled}
                  />
                )}
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => removeUpfrontScaleLine(scope, index)}
                  disabled={fieldsDisabled || scaleLines.length === 1}
                  aria-label="Remove scale line"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Group justify="flex-start">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconPlus size={14} />}
                disabled={fieldsDisabled}
                onClick={() => addUpfrontScaleLine(scope)}
              >
                Add Scale Line
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    );
  };

  const buildScaleLines = lines =>
    lines
      .filter(l => l.revenue_amount !== "" || l.percentage !== "")
      .map(l => ({
        revenue_amount: Number(l.revenue_amount),
        percentage: Number(l.percentage),
      }));

  const buildScalePayload = (templateId, lines) => ({
    template_id: templateId != null ? Number(templateId) : null,
    scale_lines: buildScaleLines(lines),
  });

  const allMarketingPartnerIds = () =>
    marketingPartners.map(mp => Number(mpId(mp)));

  // A single scale wrapped as the one-element array the API expects, applying
  // to all marketing partners (used where partner-specific scales don't apply,
  // e.g. per-user split commission and custom overrides).
  const singleScaleArray = (templateId, lines) => [
    {
      ...buildScalePayload(templateId, lines),
      marketing_partner_ids: allMarketingPartnerIds(),
    },
  ];

  // Build the array of scales for a type, each carrying the marketing partners
  // it applies to. When "apply to all" is on, a single scale covering every
  // marketing partner is sent.
  const buildScalesPayload = (type, templateId, lines) => {
    if (scaleApplyAll[type]) {
      return [
        {
          ...buildScalePayload(templateId, lines),
          marketing_partner_ids: allMarketingPartnerIds(),
        },
      ];
    }
    return [
      {
        ...buildScalePayload(templateId, lines),
        marketing_partner_ids: scalePartnerIds[type].map(Number),
      },
      ...extraScales[type].map(s => ({
        ...buildScalePayload(s.templateId, s.lines),
        marketing_partner_ids: s.marketing_partner_ids.map(Number),
      })),
    ];
  };

  // Upfront pay for a scope: the selected option id plus any edited values
  // (fixed amount / percentage / scale lines) relevant to the option's type.
  const buildUpfrontPayload = scope => {
    const id = scope
      ? (upfrontPayByUser[scope] ?? defaultUpfrontId)
      : selectedUpfrontPayId;
    const option = upfrontOptionById(id);
    const valuesKey = scope ?? UPFRONT_SINGLE_SCOPE;
    const values = upfrontValuesForScope(valuesKey, option);
    const type = option?.upfront_pay_type;
    const payload = { upfront_pay_id: id != null ? Number(id) : null };

    if (type === "FIXED_AMOUNT_PER_ACCOUNT") {
      payload.upfront_fixed_amount =
        values.fixedAmount === "" || values.fixedAmount == null
          ? null
          : Number(values.fixedAmount);
    } else if (type === "FIXED_PERCENT_ACV") {
      payload.upfront_fixed_percentage =
        values.fixedPercentage === "" || values.fixedPercentage == null
          ? null
          : Number(values.fixedPercentage);
    } else if (UPFRONT_SCALE_TYPES.includes(type)) {
      const isPercent = type === "SCALE_PERCENT_ACV";
      payload.upfront_scale_lines = (
        Array.isArray(values.scaleLines) ? values.scaleLines : []
      ).map(l => ({
        accounts_amount:
          l.accounts_amount === "" || l.accounts_amount == null
            ? null
            : Number(l.accounts_amount),
        amount:
          isPercent || l.amount === "" || l.amount == null
            ? null
            : Number(l.amount),
        percentage:
          !isPercent || l.percentage === "" || l.percentage == null
            ? null
            : Number(l.percentage),
      }));
    }

    return payload;
  };

  // Build a single user's portion of the deal. `scope` is the per-user state
  // key for split deals, or null to read the global (single-user) state. Each
  // entry is tied to either a user (user_id) or a recruiting lead (lead_id).
  const buildUserEntry = (userId, scope, percentage, leadId = null) => {
    const entry = {
      user_id: leadId != null ? null : userId != null ? Number(userId) : null,
      lead_id: leadId != null ? Number(leadId) : null,
      percentage,
      ...buildUpfrontPayload(scope),
      // Split deals carry one per-user commission scale; otherwise the scale can
      // be split across marketing partners.
      commission_scales: scope
        ? singleScaleArray(
            commissionTemplateByUser[scope] ?? null,
            commissionLinesByUser[scope] ?? []
          )
        : buildScalesPayload(
            "COMMISSION",
            commissionTemplateId,
            commissionLines
          ),
    };
    if (dealType === "REP") {
      entry.has_override_on_recruited_reps = hasOverrideOnRecruitedReps;
      entry.override_scales = hasOverrideOnRecruitedReps
        ? buildScalesPayload("OVERRIDE", overrideTemplateId, overrideLines)
        : [];
      entry.custom_overrides = hasOverrideOnRecruitedReps
        ? customUserOverrides.map(o => ({
            user_id: o.userId != null ? Number(o.userId) : null,
            override_scales: singleScaleArray(o.templateId, o.lines),
          }))
        : [];
    }
    return entry;
  };

  const buildDealPayload = () => {
    const payload = {
      selling_year_id: selectedSellingYearId,
      parent_md_id: selectedParentMdId,
      deal_type: dealType,
      default_deal_id:
        selectedDefaultDealId != null ? Number(selectedDefaultDealId) : null,
    };

    if (dealType === "MD") {
      payload.minimum_override = minimumOverride;
      payload.md_scales = buildScalesPayload("MD", mdTemplateId, mdLines);
      payload.split_md = splitMd;

      if (minimumOverride) {
        payload.minimum_override_percentage =
          minimumOverridePercentage === ""
            ? null
            : Number(minimumOverridePercentage);
        payload.minimum_override_parent_mds = minimumOverrideMdIds.map(id => ({
          id: Number(id),
          percentage: Number(minimumOverridePercentages[id]) || 0,
        }));
        payload.custom_minimum_overrides = customMinimumOverrides.map(o => ({
          user_id: o.userId != null ? Number(o.userId) : null,
          minimum_override_percentage:
            o.percentage === "" ? null : Number(o.percentage),
          parent_mds: o.parentMdIds.map(id => ({
            id: Number(id),
            percentage: Number(o.parentMdPercentages[id]) || 0,
          })),
        }));
      }
    }

    // Always send users as an array: one entry for a single deal, multiple
    // entries for a split MD deal.
    if (dealType === "MD" && splitMd) {
      payload.users = splitParticipantIds.map(userId =>
        buildUserEntry(userId, userId, Number(splitMdPercentages[userId]) || 0)
      );
    } else if (dealFor === "LEAD") {
      payload.users = [buildUserEntry(null, null, 100, selectedLeadId)];
    } else {
      payload.users = [buildUserEntry(selectedUserId, null, 100)];
    }

    return payload;
  };

  const handleSaveDeal = async () => {
    try {
      setSavingDeal(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(isEdit ? `/v1/pyr/deal/${dealId}` : "/v1/pyr/deal"),
        {
          method: isEdit ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(buildDealPayload()),
        }
      );

      if (response.ok) {
        showSuccess(
          isEdit ? "Deal updated successfully!" : "Deal saved successfully!"
        );
        navigate(-1);
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message ||
            (isEdit ? "Failed to update deal" : "Failed to save deal")
        );
      }
    } catch (err) {
      handleGenericError(
        err,
        isEdit ? "Failed to update deal" : "Failed to save deal"
      );
    } finally {
      setSavingDeal(false);
    }
  };

  const handleDeleteDeal = async () => {
    try {
      setDeletingDeal(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(getApiEndpoint(`/v1/pyr/deal/${dealId}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        showSuccess("Deal deleted successfully!");
        setConfirmDeleteOpen(false);
        navigate(
          user?.role === "COMPANY_ADMIN"
            ? "/company-downline?tab=company-revenue"
            : "/downline?tab=revenue"
        );
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to delete deal"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to delete deal");
    } finally {
      setDeletingDeal(false);
    }
  };

  // Prominent, evenly-spaced divider used to separate the major form sections.
  const sectionDivider = label => (
    <Divider
      label={
        <Text fw={700} size="md" c="dark.4">
          {label}
        </Text>
      }
      labelPosition="left"
      mt="xl"
    />
  );

  // Renders one section of the appended page from its data descriptor.
  const renderAppendedSection = section => {
    if (section.type === "text") {
      return (
        <div key={section.id}>
          {section.title && (
            <div style={appendedPageStyles.sectionLabel}>{section.title}</div>
          )}
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {section.body}
          </Text>
        </div>
      );
    }
    if (section.type === "table") {
      return (
        <div key={section.id}>
          {section.title && (
            <div style={appendedPageStyles.sectionLabel}>{section.title}</div>
          )}
          <table style={appendedPageStyles.table}>
            <thead>
              <tr>
                {section.columns.map(col => (
                  <th
                    key={col.key}
                    style={{
                      ...appendedPageStyles.th,
                      textAlign: col.align ?? "left",
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, i) => (
                <tr key={i}>
                  {section.columns.map(col => (
                    <td
                      key={col.key}
                      style={{
                        ...appendedPageStyles.td,
                        textAlign: col.align ?? "left",
                      }}
                    >
                      {row[col.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  // Custom page appended to the bottom of the document preview. The content is
  // produced by buildAppendedPageContent (shared with the backend PDF
  // generator); this only handles presentation. Updates in real time as the
  // form fields change.
  // Document text of the selected scale template for a given type, used to
  // show that text above the scale on the appended page.
  const scaleDocumentText = (type, templateId) => {
    if (templateId == null) return undefined;
    const templates = Array.isArray(defaultScales?.[type])
      ? defaultScales[type]
      : [];
    return templates.find(t => String(t.id) === String(templateId))
      ?.document_text;
  };

  const renderAppendedPage = () => {
    // The preview mirrors the form: nothing shows until a default deal is
    // selected (which is what reveals the rest of the fields).
    if (!selectedDefaultDealId) return null;

    const upfrontOption = upfrontOptionById(selectedUpfrontPayId);
    const upfrontValues = upfrontValuesForScope(
      UPFRONT_SINGLE_SCOPE,
      upfrontOption
    );
    // Only feed the preview lines for fields that are actually visible in the
    // form — hidden fields (e.g. a disabled override) must not surface their
    // pre-filled defaults in the preview.
    const showCommission = !(dealType === "MD" && splitMd);
    const showOverride = dealType === "REP" && hasOverrideOnRecruitedReps;
    const { sections } = buildAppendedPageContent({
      dealType,
      mdLines,
      commissionLines: showCommission ? commissionLines : [],
      overrideLines: showOverride ? overrideLines : [],
      mdDocumentText: scaleDocumentText("MD", mdTemplateId),
      commissionDocumentText: scaleDocumentText(
        "COMMISSION",
        commissionTemplateId
      ),
      overrideDocumentText: scaleDocumentText("OVERRIDE", overrideTemplateId),
      upfront: {
        documentText: upfrontOption?.document_text,
        type: upfrontOption?.upfront_pay_type,
        fixedAmount: upfrontValues.fixedAmount,
        fixedPercentage: upfrontValues.fixedPercentage,
        scaleLines: upfrontValues.scaleLines,
      },
    });

    if (sections.length === 0) return null;

    return (
      <>
        <Divider
          label={
            <Text size="xs" c="dimmed">
              Appended Page
            </Text>
          }
          labelPosition="center"
        />
        <Paper withBorder radius="sm" style={appendedPageStyles.page}>
          <Stack gap="xl">{sections.map(renderAppendedSection)}</Stack>
        </Paper>
      </>
    );
  };

  return (
    <PageContent title={isEdit ? "Edit Deal" : "Add Deal"}>
      <Container size="xl" style={{ maxWidth: "100%", padding: "24px" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Group>

          <Grid gutter="lg" align="flex-start">
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="lg">
                <Paper shadow="sm" p="lg" radius="md" pos="relative">
                  <LoadingOverlay
                    visible={loadingDealOptions || loadingDeal}
                    zIndex={1000}
                    overlayProps={{ radius: "md", blur: 2 }}
                  />
                  <Stack gap="xl">
                    <Title order={3}>Deal Details</Title>

                    <Select
                      label="Selling Year"
                      placeholder="Select selling year"
                      value={
                        selectedSellingYearId
                          ? String(selectedSellingYearId)
                          : null
                      }
                      onChange={value => {
                        if (!value) return;
                        const match = sellingYears.find(
                          y => String(y.id) === value
                        );
                        setSelectedSellingYearId(match ? match.id : null);
                      }}
                      data={sellingYears
                        .filter(y => !y.is_locked)
                        .map(y => ({
                          value: String(y.id),
                          label: y.name,
                        }))}
                      allowDeselect={false}
                      searchable={false}
                    />

                    <Select
                      label="Default Deal"
                      placeholder="Select default deal"
                      value={selectedDefaultDealId}
                      onChange={handleDefaultDealChange}
                      data={defaultDeals.map(d => ({
                        value: String(d.id),
                        label: d.name,
                      }))}
                      searchable
                    />

                    <Stack gap="xs">
                      <Text fw={500} size="sm">
                        Deal For
                      </Text>
                      <SegmentedControl
                        value={dealFor}
                        onChange={setDealFor}
                        disabled={isEdit}
                        data={[
                          { value: "USER", label: "Current User" },
                          { value: "LEAD", label: "Lead" },
                        ]}
                      />
                    </Stack>

                    {dealFor === "USER" ? (
                      <Select
                        label="User"
                        placeholder="Select user"
                        value={selectedUserId ? String(selectedUserId) : null}
                        onChange={value => {
                          if (!value) {
                            setSelectedUserId(null);
                            return;
                          }
                          const match = users.find(u => String(u.id) === value);
                          setSelectedUserId(match ? match.id : null);
                          setHasOverrideOnRecruitedReps(
                            !!(match && match.has_override_on_recruited_reps)
                          );
                        }}
                        data={users.map(u => ({
                          value: String(u.id),
                          label: userLabel(u),
                        }))}
                        renderOption={renderUserOption}
                        searchable
                        disabled={isEdit}
                      />
                    ) : (
                      <Select
                        label="Lead"
                        placeholder="Select lead"
                        value={selectedLeadId ? String(selectedLeadId) : null}
                        onChange={value => {
                          if (!value) {
                            setSelectedLeadId(null);
                            return;
                          }
                          const match = leads.find(l => String(l.id) === value);
                          setSelectedLeadId(match ? match.id : null);
                        }}
                        data={leads.map(l => ({
                          value: String(l.id),
                          label: leadLabel(l),
                        }))}
                        searchable
                        disabled={isEdit}
                      />
                    )}

                    <Select
                      label="Parent MD"
                      placeholder="Select parent MD"
                      value={
                        selectedParentMdId ? String(selectedParentMdId) : null
                      }
                      onChange={value => {
                        if (!value) {
                          setSelectedParentMdId(null);
                          return;
                        }
                        const match = mdList.find(
                          md => String(md.id) === value
                        );
                        setSelectedParentMdId(match ? match.id : null);
                      }}
                      data={mdList.map(md => ({
                        value: String(md.id),
                        label: mdLabel(md),
                      }))}
                      renderOption={renderMdOption}
                      searchable
                    />

                    {selectedDefaultDealId && (
                      <>
                        {dealType === "MD" && (
                          <Switch
                            label="Split MD"
                            checked={splitMd}
                            onChange={event =>
                              setSplitMd(event.currentTarget.checked)
                            }
                            disabled={fieldsDisabled}
                          />
                        )}

                        {dealType === "MD" && splitMd && (
                          <>
                            <MultiSelect
                              label="Split Users"
                              placeholder="Select users"
                              value={splitMdUserIds}
                              onChange={setSplitMdUserIds}
                              data={users.map(u => ({
                                value: String(u.id),
                                label: userLabel(u),
                              }))}
                              searchable
                            />

                            {(() => {
                              const total = splitParticipantIds.reduce(
                                (sum, id) =>
                                  sum + (Number(splitMdPercentages[id]) || 0),
                                0
                              );
                              return (
                                <>
                                  {splitParticipantIds.map(userId => {
                                    const user = usersById[userId];
                                    return (
                                      <Group
                                        key={userId}
                                        wrap="nowrap"
                                        align="center"
                                        gap="sm"
                                      >
                                        <Avatar
                                          src={user?.profile_image_url}
                                          radius="xl"
                                          size="sm"
                                        >
                                          {user
                                            ? userLabel(user).charAt(0)
                                            : "?"}
                                        </Avatar>
                                        <Text size="sm" style={{ flex: 1 }}>
                                          {user ? userLabel(user) : "User"}
                                        </Text>
                                        <NumberInput
                                          placeholder="Percentage"
                                          value={
                                            splitMdPercentages[userId] ?? ""
                                          }
                                          onChange={value =>
                                            setSplitMdPercentages(prev => ({
                                              ...prev,
                                              [userId]: value,
                                            }))
                                          }
                                          min={0}
                                          suffix="%"
                                          required
                                          w={140}
                                        />
                                      </Group>
                                    );
                                  })}
                                  <Group justify="space-between">
                                    <Text fw={600} size="sm">
                                      Total
                                    </Text>
                                    <Text
                                      fw={700}
                                      c={total === 100 ? "green.7" : "red.7"}
                                    >
                                      {total}%
                                    </Text>
                                  </Group>
                                </>
                              );
                            })()}
                          </>
                        )}

                        {sectionDivider("Scales")}

                        {dealType === "MD" && (
                          <>
                            {renderScaleSection({
                              type: "MD",
                              label: "MD Scale",
                              templateId: mdTemplateId,
                              setTemplateId: setMdTemplateId,
                              lines: mdLines,
                              setLines: setMdLines,
                              required: true,
                              validateAgainstParent: true,
                            })}
                            {renderMarketingPartnerScales("MD", "MD Scale")}
                          </>
                        )}

                        {dealType === "MD" && splitMd ? (
                          <Tabs
                            value={
                              splitActiveTab &&
                              splitParticipantIds.includes(splitActiveTab)
                                ? splitActiveTab
                                : splitParticipantIds[0]
                            }
                            onChange={setSplitActiveTab}
                            mt="sm"
                          >
                            <Tabs.List>
                              {splitParticipantIds.map(userId => {
                                const user = usersById[userId];
                                return (
                                  <Tabs.Tab key={userId} value={userId}>
                                    {user ? userLabel(user) : "User"}
                                  </Tabs.Tab>
                                );
                              })}
                            </Tabs.List>

                            {splitParticipantIds.map(userId => (
                              <Tabs.Panel key={userId} value={userId} pt="md">
                                <Stack gap="xl">
                                  {renderCommissionScaleForUser(userId)}

                                  {sectionDivider("Upfront Pay")}
                                  {renderUpfrontPaySection(userId)}
                                </Stack>
                              </Tabs.Panel>
                            ))}
                          </Tabs>
                        ) : (
                          <>
                            {renderScaleSection({
                              type: "COMMISSION",
                              label: "Commission Scale",
                              templateId: commissionTemplateId,
                              setTemplateId: setCommissionTemplateId,
                              lines: commissionLines,
                              setLines: setCommissionLines,
                              required: true,
                              validateAgainstParent: true,
                            })}
                            {renderMarketingPartnerScales(
                              "COMMISSION",
                              "Commission Scale"
                            )}

                            {dealType === "REP" && (
                              <Switch
                                label="Allow override on all recruited reps"
                                checked={hasOverrideOnRecruitedReps}
                                onChange={event =>
                                  setHasOverrideOnRecruitedReps(
                                    event.currentTarget.checked
                                  )
                                }
                                disabled={fieldsDisabled}
                              />
                            )}

                            {dealType === "REP" &&
                              hasOverrideOnRecruitedReps && (
                                <>
                                  {renderScaleSection({
                                    type: "OVERRIDE",
                                    label: "Override Scale",
                                    templateId: overrideTemplateId,
                                    setTemplateId: setOverrideTemplateId,
                                    lines: overrideLines,
                                    setLines: setOverrideLines,
                                    required: true,
                                  })}
                                  {renderMarketingPartnerScales(
                                    "OVERRIDE",
                                    "Override Scale"
                                  )}

                                  {overrideUsersList.length > 0 && (
                                    <Stack gap="xs">
                                      <Text fw={600} size="sm">
                                        Override Users
                                      </Text>
                                      {overrideUsersList.map(u => (
                                        <Group
                                          key={u.id}
                                          gap="sm"
                                          wrap="nowrap"
                                          align="center"
                                        >
                                          <Avatar
                                            src={u.profile_image_url}
                                            radius="xl"
                                            size="sm"
                                          >
                                            {(u.first_name || "?").charAt(0)}
                                          </Avatar>
                                          <Text size="sm" style={{ flex: 1 }}>
                                            {userLabel(u)}
                                          </Text>
                                          {u.has_deal === false && (
                                            <Tooltip label="User doesn't have a deal for this selling year">
                                              <IconAlertTriangle
                                                size={18}
                                                color="var(--mantine-color-yellow-6)"
                                              />
                                            </Tooltip>
                                          )}
                                        </Group>
                                      ))}
                                    </Stack>
                                  )}

                                  {customUserOverrides.map(
                                    (override, index) => (
                                      <Paper
                                        key={override.key}
                                        withBorder
                                        p="sm"
                                        radius="md"
                                      >
                                        <Stack gap="sm">
                                          <Group
                                            justify="space-between"
                                            align="center"
                                          >
                                            <Text size="sm" fw={600}>
                                              Custom Override for User
                                            </Text>
                                            <ActionIcon
                                              variant="subtle"
                                              color="red"
                                              onClick={() =>
                                                removeCustomUserOverride(index)
                                              }
                                              aria-label="Remove custom override"
                                            >
                                              <IconTrash size={16} />
                                            </ActionIcon>
                                          </Group>

                                          <Select
                                            label="User"
                                            placeholder="Select user"
                                            data={recruitedRepsOptions}
                                            value={override.userId}
                                            onChange={value =>
                                              updateCustomUserOverride(index, {
                                                userId: value,
                                              })
                                            }
                                            searchable
                                          />

                                          {renderScaleSection({
                                            type: "OVERRIDE",
                                            label: "Override Scale",
                                            templateId: override.templateId,
                                            setTemplateId: value =>
                                              updateCustomUserOverride(index, {
                                                templateId: value,
                                              }),
                                            lines: override.lines,
                                            setLines: updater =>
                                              updateCustomUserOverride(index, {
                                                lines:
                                                  typeof updater === "function"
                                                    ? updater(override.lines)
                                                    : updater,
                                              }),
                                            required: true,
                                          })}
                                        </Stack>
                                      </Paper>
                                    )
                                  )}

                                  {!fieldsDisabled && (
                                    <Group>
                                      <Button
                                        variant="light"
                                        size="xs"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={addCustomUserOverride}
                                      >
                                        Add Custom Override for User
                                      </Button>
                                    </Group>
                                  )}
                                </>
                              )}

                            {sectionDivider("Upfront Pay")}
                            {renderUpfrontPaySection()}
                          </>
                        )}

                        {dealType === "MD" && (
                          <>
                            {sectionDivider("Other Options")}

                            <Switch
                              label="Minimum Override"
                              checked={minimumOverride}
                              onChange={event =>
                                setMinimumOverride(event.currentTarget.checked)
                              }
                            />

                            {minimumOverride && (
                              <>
                                <NumberInput
                                  label="Minimum Override Percentage"
                                  placeholder="Enter minimum override percentage"
                                  value={minimumOverridePercentage}
                                  onChange={setMinimumOverridePercentage}
                                  min={0}
                                  suffix="%"
                                />

                                <MultiSelect
                                  label="Parent MDs"
                                  placeholder="Select parent MDs"
                                  value={minimumOverrideMdIds}
                                  onChange={setMinimumOverrideMdIds}
                                  data={mdList.map(md => ({
                                    value: String(md.id),
                                    label: mdLabel(md),
                                  }))}
                                  searchable
                                />

                                {renderParentMdRows(
                                  minimumOverrideMdIds,
                                  minimumOverridePercentages,
                                  (id, value) =>
                                    setMinimumOverridePercentages(prev => ({
                                      ...prev,
                                      [id]: value,
                                    })),
                                  Number(minimumOverridePercentage) || 0
                                )}

                                <Text size="sm" c="dimmed">
                                  This minimum override applies to all users in{" "}
                                  {selectedUser
                                    ? `${userLabel(selectedUser)}'s`
                                    : "their"}{" "}
                                  downline for the year. For specific users you
                                  can add a custom override.
                                </Text>

                                {customMinimumOverrides.map(
                                  (override, index) => (
                                    <Paper
                                      key={override.key}
                                      withBorder
                                      p="sm"
                                      radius="md"
                                    >
                                      <Stack gap="sm">
                                        <Group
                                          justify="space-between"
                                          align="center"
                                        >
                                          <Text size="sm" fw={600}>
                                            Custom Minimum Override
                                          </Text>
                                          <ActionIcon
                                            variant="subtle"
                                            color="red"
                                            onClick={() =>
                                              removeCustomMinimumOverride(index)
                                            }
                                            aria-label="Remove custom minimum override"
                                          >
                                            <IconTrash size={16} />
                                          </ActionIcon>
                                        </Group>

                                        <Select
                                          label="User"
                                          placeholder="Select user"
                                          data={downlineUserOptions}
                                          value={override.userId}
                                          onChange={value =>
                                            updateCustomMinimumOverride(index, {
                                              userId: value,
                                            })
                                          }
                                          searchable
                                        />

                                        <NumberInput
                                          label="Minimum Override Percentage"
                                          placeholder="Enter minimum override percentage"
                                          value={override.percentage}
                                          onChange={value =>
                                            updateCustomMinimumOverride(index, {
                                              percentage: value,
                                            })
                                          }
                                          min={0}
                                          suffix="%"
                                        />

                                        <MultiSelect
                                          label="Parent MDs"
                                          placeholder="Select parent MDs"
                                          data={mdList.map(md => ({
                                            value: String(md.id),
                                            label: mdLabel(md),
                                          }))}
                                          value={override.parentMdIds}
                                          onChange={value =>
                                            updateCustomMinimumOverride(index, {
                                              parentMdIds: value,
                                            })
                                          }
                                          searchable
                                        />

                                        {renderParentMdRows(
                                          override.parentMdIds,
                                          override.parentMdPercentages,
                                          (id, value) =>
                                            updateCustomMinimumOverride(index, {
                                              parentMdPercentages: {
                                                ...override.parentMdPercentages,
                                                [id]: value,
                                              },
                                            }),
                                          Number(override.percentage) || 0
                                        )}
                                      </Stack>
                                    </Paper>
                                  )
                                )}

                                <Group>
                                  <Button
                                    variant="light"
                                    size="xs"
                                    leftSection={<IconPlus size={14} />}
                                    onClick={addCustomMinimumOverride}
                                  >
                                    Add Custom Minimum Override
                                  </Button>
                                </Group>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Stack>
                </Paper>

                <Group justify={isEdit ? "space-between" : "flex-end"}>
                  {isEdit && (
                    <Button
                      color="red"
                      variant="outline"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => setConfirmDeleteOpen(true)}
                      disabled={savingDeal || deletingDeal}
                    >
                      Delete Deal
                    </Button>
                  )}
                  <Button onClick={handleSaveDeal} loading={savingDeal}>
                    {isEdit ? "Update Deal" : "Save Deal"}
                  </Button>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Paper shadow="sm" p="lg" radius="md" pos="sticky" top={16}>
                <Stack gap="md">
                  <Title order={4}>Deal Preview</Title>
                  {renderAppendedPage()}
                </Stack>
              </Paper>
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>

      <Modal
        opened={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title="Delete Deal"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this deal? This action cannot be
            undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={deletingDeal}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteDeal}
              loading={deletingDeal}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
}

function AddDealPage() {
  return <DealFormPage mode="add" />;
}

export { DealFormPage };
export default AddDealPage;
